import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import type { RefresherCustomEvent } from '@ionic/react';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_LEAGUE_EVENTS } from '../../../graphql/queries/leagueEvents';
import { UPDATE_ACTIVITY_LAST_READ } from '../../../graphql/mutations/updateActivityLastRead';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';
import { GET_FANTASY_LEAGUE_TABS_SHELL } from '../../../graphql/queries/fantasyLeagueTabsShell';
import type {
  GetLeagueEventsQuery,
  GetLeagueEventsQueryVariables,
  UpdateActivityLastReadMutation,
  UpdateActivityLastReadMutationVariables,
} from '../../../types/graphql-generated';
import { LeagueEventCard } from './components/LeagueEventCard';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LeagueSettingsButton } from '../components/LeagueSettingsButton';
import { groupByDate } from '../utils/dateGrouping';

interface ActivityTabProps {
  leagueId: string;
  memberId?: string;
  activityLastReadAt?: string | null;
}

const EVENTS_PER_PAGE = 20;
const MARK_AS_READ_DELAY_MS = 2000;

export const ActivityTab = ({
  leagueId,
  memberId,
  activityLastReadAt,
}: ActivityTabProps) => {
  const [eventsOffset, setEventsOffset] = useState(0);
  const readTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();
  const isViewActive = pathname === `/app/ligas/${leagueId}/actividad`;
  const { data: eventsData, loading, error, fetchMore, refetch } = useQuery<
    GetLeagueEventsQuery,
    GetLeagueEventsQueryVariables
  >(GET_LEAGUE_EVENTS, {
    variables: {
      fantasyLeagueId: leagueId,
      limit: EVENTS_PER_PAGE,
      offset: 0,
    },
  });

  const [updateActivityLastRead] = useMutation<
    UpdateActivityLastReadMutation,
    UpdateActivityLastReadMutationVariables
  >(UPDATE_ACTIVITY_LAST_READ, {
    refetchQueries: [
      { query: GET_MY_FANTASY_LEAGUES },
      { query: GET_FANTASY_LEAGUE_TABS_SHELL, variables: { fantasyLeagueId: leagueId } },
    ],
  });

  // Ionic keeps tab pages mounted and does not reliably emit view-enter events
  // with this nested router. The location changes for both direct loads and tab
  // navigation, so use it as the source of truth for whether this view is active.
  useEffect(() => {
    if (!isViewActive || !memberId) return;

    if (readTimerRef.current) {
      clearTimeout(readTimerRef.current);
    }

    readTimerRef.current = setTimeout(() => {
      updateActivityLastRead({
        variables: {
          input: {
            fantasyLeagueMemberId: memberId,
          },
        },
      }).catch((err) => {
        console.error('Error updating activity last read:', err);
      });
    }, MARK_AS_READ_DELAY_MS);

    return () => {
      if (readTimerRef.current) {
        clearTimeout(readTimerRef.current);
        readTimerRef.current = null;
      }
    };
  }, [isViewActive, memberId, updateActivityLastRead]);

  const events = eventsData?.fantasyLeagueEvents || [];
  const groupedEvents = groupByDate(events, (event) => event.createdAt);

  const loadMore = async (ev: CustomEvent<void>) => {
    const newOffset = eventsOffset + EVENTS_PER_PAGE;

    await fetchMore({
      variables: {
        fantasyLeagueId: leagueId,
        limit: EVENTS_PER_PAGE,
        offset: newOffset,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...prev,
          fantasyLeagueEvents: [
            ...prev.fantasyLeagueEvents,
            ...fetchMoreResult.fantasyLeagueEvents,
          ],
        };
      },
    });

    setEventsOffset(newOffset);
    (ev.target as HTMLIonInfiniteScrollElement).complete();
  };

  const onRefresh = async (ev: RefresherCustomEvent) => {
    try {
      setEventsOffset(0);
      await refetch({
        fantasyLeagueId: leagueId,
        limit: EVENTS_PER_PAGE,
        offset: 0,
      });
    } finally {
      ev.detail.complete();
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle className="ml-4">Actividad</IonTitle>
          <IonButtons slot="end">
            <LeagueSettingsButton leagueId={leagueId} />
          </IonButtons>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && events.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar la actividad</p>
          </IonText>
        )}

        {!loading && events.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonText color="medium">
              <p>No hay actividad reciente en esta liga</p>
            </IonText>
          </div>
        )}

        {events.length > 0 && (
          <>
            <div className="space-y-4">
              {groupedEvents.map((group) => (
                <div key={group.dateKey}>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2 px-2">
                    {group.dateHeader}
                  </h3>
                  <div className="space-y-2">
                    {group.items.map((event) => (
                      <LeagueEventCard
                        key={event.id}
                        event={event}
                        activityLastReadAt={activityLastReadAt}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px">
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Cargando más..."
              />
            </IonInfiniteScroll>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
